import React, { useState, useEffect, useRef } from 'react';
import { Share2, Download, X, Trophy } from 'lucide-react';
import { gamesData } from '../static/GamesConst';
import API_URL from '../config/APIURL';
export function ShareScoreModal({ show, onClose, userId }) {
  const [userScores, setUserScores] = useState([]);
  const [selectedScore, setSelectedScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const canvasRef = useRef(null);

  const [gameImage, setGameImage] = useState(null);

  useEffect(() => {
    if (show && userId) {
      fetchUserTopScores();
    }
  }, [show, userId]);

  const fetchUserTopScores = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('cugs_token');
      const response = await fetch(`${API_URL}/api/games/top-scores`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setUserScores(data.scores || []);
    } catch (error) {
      console.error('Failed to fetch scores:', error);
    }
    setLoading(false);
  };

  const getGameInfo = (gameId) => {
    const gameIdMap = {
      'cug-jump': 2,
      'brick-builder': 1,
      'neon-hades': 3,
      'neon-shadow': 4,
      'react-survivors': 5,
      'cqb-school': 6,
      'multiplier': 7,
      'paper-flight': 8
    };
    const id = gameIdMap[gameId];
    return gamesData.find(g => g.id === id) || { title: gameId };
  };

  const generateShareImage = (loadedImage) => {

    if (!selectedScore || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas size (Instagram story size)
    canvas.width = 1080;
    canvas.height = 1720;

    // Background gradient
    const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
    gradient.addColorStop(0, '#0a0a0f');
    gradient.addColorStop(0.5, '#1a0a2e');
    gradient.addColorStop(1, '#0a0a0f');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Grid pattern overlay
    ctx.strokeStyle = 'rgba(0, 245, 255, 0.05)';
    ctx.lineWidth = 2;
    for (let i = 0; i < canvas.height; i += 50) {
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(canvas.width, i);
      ctx.stroke();
    }
    for (let i = 0; i < canvas.width; i += 50) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, canvas.height);
      ctx.stroke();
    }

    // Center card
    const gameInfo = getGameInfo(selectedScore.gameId);

    // Center card
    const cardY = canvas.height / 2 - 700;
    const cardX = 140;
    const cardWidth = canvas.width - 280;
    const cardHeight = 1400;

    // Card background with glow
    ctx.shadowColor = 'rgba(0, 245, 255, 0.5)';
    ctx.shadowBlur = 40;
    ctx.fillStyle = 'rgba(10, 10, 20, 0.9)';
    ctx.fillRect(cardX, cardY, cardWidth, cardHeight);
    ctx.shadowBlur = 0;

    // Card border
    ctx.strokeStyle = 'rgba(0, 245, 255, 0.5)';
    ctx.lineWidth = 4;
    ctx.strokeRect(cardX, cardY, cardWidth, cardHeight);

    // Draw game thumbnail image at the top of the card
    if (loadedImage) {
        const imgSize = 400;
        const imgX = canvas.width / 2 - imgSize / 2;
        const imgY = cardY + 40;
        
        // Add glow effect for image
        ctx.shadowColor = 'rgba(0, 245, 255, 0.4)';
        ctx.shadowBlur = 30;
        
        // Draw rounded rectangle clip path
        ctx.save();
        const radius = 15;
        ctx.beginPath();
        ctx.moveTo(imgX + radius, imgY);
        ctx.lineTo(imgX + imgSize - radius, imgY);
        ctx.quadraticCurveTo(imgX + imgSize, imgY, imgX + imgSize, imgY + radius);
        ctx.lineTo(imgX + imgSize, imgY + imgSize - radius);
        ctx.quadraticCurveTo(imgX + imgSize, imgY + imgSize, imgX + imgSize - radius, imgY + imgSize);
        ctx.lineTo(imgX + radius, imgY + imgSize);
        ctx.quadraticCurveTo(imgX, imgY + imgSize, imgX, imgY + imgSize - radius);
        ctx.lineTo(imgX, imgY + radius);
        ctx.quadraticCurveTo(imgX, imgY, imgX + radius, imgY);
        ctx.closePath();
        ctx.clip();
        
        ctx.drawImage(loadedImage, imgX, imgY, imgSize, imgSize);
        ctx.restore();
        ctx.shadowBlur = 0;
    }

    // Game title (move down to make room for image)
    ctx.fillStyle = '#00f5ff';
    ctx.font = 'bold 70px Orbitron, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(gameInfo.title.toUpperCase(), canvas.width / 2, cardY + 590);

    // Decorative line
    ctx.strokeStyle = 'rgba(255, 0, 255, 0.5)';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(cardX + 100, cardY + 630);
    ctx.lineTo(canvas.width - cardX - 100, cardY + 630);
    ctx.stroke();
    // "HIGH SCORE" label
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.font = '600 45px Rajdhani, sans-serif';
    ctx.fillText('HIGH SCORE', canvas.width / 2, cardY + 790);

    // Score
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 160px Orbitron, sans-serif';
    ctx.fillText(selectedScore.score.toLocaleString(), canvas.width / 2, cardY + 930);

    // Username
    ctx.fillStyle = '#ff00ff';
    ctx.font = 'bold 55px Orbitron, sans-serif';
    ctx.fillText(`@${selectedScore.username}`, canvas.width / 2, cardY + 1000);

    // Trophy icon (simplified)
    ctx.fillStyle = '#FFD700';
    ctx.beginPath();
    ctx.arc(canvas.width / 2, cardY + 1150, 40, 0, Math.PI * 2);
    ctx.fill();

    // Bottom branding
    ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.font = '600 40px Orbitron, sans-serif';
    ctx.fillText('CUGS//GAMES', canvas.width / 2, cardY + 1240);

    // Date
    ctx.font = '500 30px Rajdhani, sans-serif';
    ctx.fillText(new Date(selectedScore.timestamp).toLocaleDateString(), canvas.width / 2, cardY + 1290);
  };

    useEffect(() => {
    if (selectedScore) {
        const gameInfo = getGameInfo(selectedScore.gameId);
        const img = new Image();
        img.onload = () => {
        setGameImage(img);
        generateShareImage(img);
        };
        img.onerror = () => {
        console.error('Failed to load game image');
        generateShareImage(null);
        };
        img.src = gameInfo.thumbnail;
    }
    }, [selectedScore]);

  const handleDownload = () => {
    if (!canvasRef.current) return;
    
    const link = document.createElement('a');
    link.download = `cugs-${selectedScore.gameId}-${selectedScore.score}.png`;
    link.href = canvasRef.current.toDataURL();
    link.click();
  };

  const handleShare = async () => {
    if (!canvasRef.current) return;

    try {
      const blob = await new Promise(resolve => canvasRef.current.toBlob(resolve));
      const file = new File([blob], 'score.png', { type: 'image/png' });
      
      if (navigator.share && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `My ${getGameInfo(selectedScore.gameId).title} Score`,
          text: `I scored ${selectedScore.score.toLocaleString()} on ${getGameInfo(selectedScore.gameId).title}!`
        });
      } else {
        handleDownload();
      }
    } catch (error) {
      console.error('Share failed:', error);
      handleDownload();
    }
  };

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'rgba(10, 10, 20, 0.98)',
        border: '1px solid rgba(0, 245, 255, 0.3)',
        borderRadius: '20px',
        padding: '40px',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'transparent',
            border: 'none',
            color: '#ff00ff',
            cursor: 'pointer'
          }}
        >
          <X size={28} />
        </button>

        <h2 style={{
          fontFamily: "'Orbitron', monospace",
          fontSize: '2rem',
          marginBottom: '30px',
          color: '#00f5ff',
          display: 'flex',
          alignItems: 'center',
          gap: '15px'
        }}>
          <Share2 size={32} />
          Share Your Score
        </h2>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: 'rgba(255,255,255,0.5)' }}>Loading your scores...</p>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
            {/* Score Selection */}
            <div style={{ flex: '1', minWidth: '300px' }}>
              <h3 style={{
                fontFamily: "'Rajdhani', sans-serif",
                fontSize: '1.2rem',
                marginBottom: '15px',
                color: 'rgba(255,255,255,0.7)'
              }}>
                Select a score to share:
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {userScores.map(score => {
                  const gameInfo = getGameInfo(score.gameId);
                  return (
                    <button
                      key={score.id}
                      onClick={() => setSelectedScore(score)}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '15px 20px',
                        background: selectedScore?.id === score.id 
                          ? 'linear-gradient(135deg, rgba(0, 245, 255, 0.2), rgba(255, 0, 255, 0.2))'
                          : 'rgba(255, 255, 255, 0.05)',
                        border: selectedScore?.id === score.id
                          ? '2px solid #00f5ff'
                          : '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <div style={{ textAlign: 'left' }}>
                        <div style={{
                          fontFamily: "'Rajdhani', sans-serif",
                          fontSize: '1rem',
                          fontWeight: '600',
                          color: '#fff'
                        }}>
                          {gameInfo.title}
                        </div>
                        <div style={{
                          fontSize: '0.8rem',
                          color: 'rgba(255,255,255,0.5)'
                        }}>
                          {new Date(score.timestamp).toLocaleDateString()}
                        </div>
                      </div>
                      <div style={{
                        fontFamily: "'Orbitron', monospace",
                        fontSize: '1.5rem',
                        fontWeight: '700',
                        color: '#00f5ff'
                      }}>
                        {score.score.toLocaleString()}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Preview & Actions */}
            {selectedScore && (
              <div style={{ flex: '1', minWidth: '300px', textAlign: 'center' }}>
                <h3 style={{
                  fontFamily: "'Rajdhani', sans-serif",
                  fontSize: '1.2rem',
                  marginBottom: '15px',
                  color: 'rgba(255,255,255,0.7)'
                }}>
                  Preview:
                </h3>
                <canvas
                  ref={canvasRef}
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    border: '1px solid rgba(0, 245, 255, 0.3)',
                    borderRadius: '10px',
                    marginBottom: '20px'
                  }}
                />
                <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                  <button
                    onClick={handleShare}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '15px 30px',
                      background: 'linear-gradient(135deg, #00f5ff, #ff00ff)',
                      border: 'none',
                      borderRadius: '10px',
                      color: '#000',
                      fontFamily: "'Orbitron', monospace",
                      fontSize: '1rem',
                      fontWeight: '700',
                      cursor: 'pointer'
                    }}
                  >
                    <Share2 size={20} />
                    Share
                  </button>
                  <button
                    onClick={handleDownload}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '15px 30px',
                      background: 'transparent',
                      border: '1px solid #00f5ff',
                      borderRadius: '10px',
                      color: '#00f5ff',
                      fontFamily: "'Orbitron', monospace",
                      fontSize: '1rem',
                      fontWeight: '700',
                      cursor: 'pointer'
                    }}
                  >
                    <Download size={20} />
                    Download
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}